import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { api, type OffsetPage } from '@/lib/api';
import { hasPermission, isAdminOrOwner, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { RequestSupportSessionDialog } from '@/pages/support/request-support-session-dialog';
import { CreateSupportTicketDialog } from '@/pages/support/create-support-ticket-dialog';
import {
  SupportTicketPriorityBadge,
  SupportTicketStatusBadge,
} from '@/pages/support/support-ticket-badges';
import {
  formatTicketNumber,
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketStatus,
  type SupportTicketSummary,
} from '@/pages/support/types';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatRelative } from '@/utils/format';

export const SupportTicketsPage = () => {
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const { locale, timezone } = usePreferences();
  const canCreate = hasPermission(membership, PERMISSIONS.SUPPORT_TICKETS_CREATE);
  const canRequestSession = isAdminOrOwner(membership);

  const [status, setStatus] = useState<SupportTicketStatus | ''>('');
  const [category, setCategory] = useState<SupportTicketCategory | ''>('');
  const [priority, setPriority] = useState<SupportTicketPriority | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [status, category, priority]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (priority) params.set('priority', priority);
    return params.toString();
  }, [category, page, pageSize, priority, status]);

  const list = useQuery({
    queryKey: ['support', 'tickets', status, category, priority, page, pageSize],
    queryFn: () =>
      api.get<{ data: SupportTicketSummary[]; page: OffsetPage }>(
        `/support/tickets?${queryString}`,
      ),
    placeholderData: keepPreviousData,
  });

  const hasFilters = status !== '' || category !== '' || priority !== '';
  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  const columns: DataTableColumn<SupportTicketSummary>[] = [
    {
      id: 'number',
      header: t('columns.number'),
      cell: (row) => t('ticketNumber', { number: formatTicketNumber(row.number) }),
    },
    {
      id: 'subject',
      header: t('columns.subject'),
      cell: (row) => <span className="font-medium text-fg">{row.subject}</span>,
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => <SupportTicketStatusBadge status={row.status} />,
    },
    {
      id: 'priority',
      header: t('columns.priority'),
      cell: (row) => <SupportTicketPriorityBadge priority={row.priority} />,
    },
    {
      id: 'lastMessageAt',
      header: t('columns.lastActivity'),
      cell: (row) => (
        <time
          dateTime={row.lastMessageAt}
          title={formatDateTime(row.lastMessageAt, { locale, timezone, withZone: true })}
          className="text-body-sm text-fg-secondary"
        >
          {formatRelative(row.lastMessageAt, { locale, timezone })}
        </time>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        headerActions={
          canCreate || canRequestSession ? (
            <div className="flex flex-wrap justify-end gap-2">
              {canRequestSession ? (
                <Button variant="outline" onClick={() => setSessionOpen(true)}>
                  {t('session.request')}
                </Button>
              ) : null}
              {canCreate ? (
                <Button onClick={() => setCreateOpen(true)}>{t('create.action')}</Button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1">
          <p className="mb-1 text-caption text-fg-secondary">{t('filters.status')}</p>
          <Listbox
            value={status}
            onChange={(value) => setStatus(value as SupportTicketStatus | '')}
            options={[
              { value: '', label: t('filters.allStatuses') },
              ...SUPPORT_TICKET_STATUSES.map((value) => ({
                value,
                label: t(`status.${value}`),
              })),
            ]}
          />
        </div>
        <div className="min-w-[10rem] flex-1">
          <p className="mb-1 text-caption text-fg-secondary">{t('filters.category')}</p>
          <Listbox
            value={category}
            onChange={(value) => setCategory(value as SupportTicketCategory | '')}
            options={[
              { value: '', label: t('filters.allCategories') },
              ...SUPPORT_TICKET_CATEGORIES.map((value) => ({
                value,
                label: t(`category.${value}`),
              })),
            ]}
          />
        </div>
        <div className="min-w-[10rem] flex-1">
          <p className="mb-1 text-caption text-fg-secondary">{t('filters.priority')}</p>
          <Listbox
            value={priority}
            onChange={(value) => setPriority(value as SupportTicketPriority | '')}
            options={[
              { value: '', label: t('filters.allPriorities') },
              ...SUPPORT_TICKET_PRIORITIES.map((value) => ({
                value,
                label: t(`priority.${value}`),
              })),
            ]}
          />
        </div>
        {hasFilters ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStatus('');
              setCategory('');
              setPriority('');
            }}
          >
            {t('filters.clear')}
          </Button>
        ) : null}
      </div>

      {list.isError ? (
        <p className="text-body-sm text-danger">{tCommon('state.errorBody')}</p>
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {hasFilters ? t('list.filteredEmpty') : t('list.empty')}
              </p>
            }
            onRowClick={(row) => void navigate(`/support/tickets/${row.id}`)}
          />
          {pageMeta && pageMeta.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={pageMeta.page}
                pageSize={pageMeta.pageSize}
                totalItems={pageMeta.totalItems}
                totalPages={pageMeta.totalPages}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                labels={{
                  showing: ({ from, to, total }) =>
                    tCommon('pagination.showing', { from, to, total }),
                  perPage: tCommon('pagination.perPage'),
                  first: tCommon('pagination.first'),
                  previous: tCommon('pagination.previous'),
                  next: tCommon('pagination.next'),
                  last: tCommon('pagination.last'),
                }}
              />
            </div>
          ) : null}
        </>
      )}

      <CreateSupportTicketDialog open={createOpen} onOpenChange={setCreateOpen} />
      {canRequestSession ? (
        <RequestSupportSessionDialog open={sessionOpen} onOpenChange={setSessionOpen} />
      ) : null}
    </div>
  );
};
